import { useEffect, useMemo, useState } from "react";
import { Icons } from "@admin/components/icons/Icons";
import { AdminTable } from "@admin/components/shared/AdminTable";
import { Modal } from "@admin/components/ui/Modal";
import { HeroCarouselForm } from "@admin/components/modals/HeroCarouselForm";
import { StandardCarouselForm } from "@admin/components/modals/StandardCarouselForm";
import { Pagination } from "@admin/components/ui/Pagination";
import {
  useAdminHeroCarousels,
  useAdminStandardCarousels,
  useDebounce,
  useNotifications,
  useToggleHeroCarousel,
  useToggleStandardCarousel,
} from "@admin/hooks";
import { useInvalidateHomeCache } from "@shared/hooks/useHome";
import type {
  HeroCarouselRecord,
  StandardCarouselRecord,
} from "@shared/types/carousel";

type TabKey = "hero" | "standard";

type HeroCarouselRow = HeroCarouselRecord & { type: "hero" };
type StandardCarouselRow = StandardCarouselRecord & { type: "standard" };
type CarouselRow = HeroCarouselRow | StandardCarouselRow;

export function AdminCarouselsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("standard");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<TabKey>("standard");
  const [editingCarousel, setEditingCarousel] = useState<CarouselRow | undefined>(
    undefined
  );
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [carouselToToggle, setCarouselToToggle] = useState<CarouselRow | undefined>(
    undefined
  );
  const [isSubmittingToggle, setIsSubmittingToggle] = useState(false);
  const [heroPage, setHeroPage] = useState(1);
  const [standardPage, setStandardPage] = useState(1);
  const pageSize = 10;

  const { showSuccess, showError } = useNotifications();
  const { mutate: invalidateHomeCache, isPending: isInvalidatingCache } = useInvalidateHomeCache();

  // Aplicar debounce al searchQuery (500ms de delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (debouncedSearchQuery) {
      if (activeTab === "hero") {
        setHeroPage(1);
      } else {
        setStandardPage(1);
      }
    }
  }, [debouncedSearchQuery, activeTab]);

  const {
    data: heroCarouselsResponse,
    isLoading: isLoadingHero,
    isError: isErrorHero,
    error: heroError,
  } = useAdminHeroCarousels({
    page: heroPage,
    limit: pageSize,
    search: debouncedSearchQuery || undefined,
  });

  const {
    data: standardCarouselsResponse,
    isLoading: isLoadingStandard,
    isError: isErrorStandard,
    error: standardError,
  } = useAdminStandardCarousels({
    page: standardPage,
    limit: pageSize,
    search: debouncedSearchQuery || undefined,
    includeProducts: true,
  });

  const heroCarousels: HeroCarouselRow[] = useMemo(
    () =>
      (heroCarouselsResponse?.data ?? []).map((carousel) => ({
        ...carousel,
        type: "hero" as const,
      })),
    [heroCarouselsResponse?.data]
  );

  const standardCarousels: StandardCarouselRow[] = useMemo(
    () =>
      (standardCarouselsResponse?.data ?? []).map((carousel) => ({
        ...carousel,
        type: "standard" as const,
      })),
    [standardCarouselsResponse?.data]
  );

  const heroPagination = heroCarouselsResponse?.pagination;
  const standardPagination = standardCarouselsResponse?.pagination;

  const { mutateAsync: toggleHeroCarousel } = useToggleHeroCarousel();
  const { mutateAsync: toggleStandardCarousel } = useToggleStandardCarousel();

  // Columnas de la tabla
  const heroColumns = [
    {
      header: "NOMBRE",
      accessor: "name" as const,
    },
    {
      header: "DESCRIPCIÓN",
      accessor: (row: HeroCarouselRow) =>
        row.description ? (
          <span className="text-sm text-gray-600 line-clamp-1">{row.description}</span>
        ) : (
          <span className="text-sm text-gray-400">Sin descripción</span>
        ),
    },
    {
      header: "SLIDES",
      accessor: (row: HeroCarouselRow) => (
        <span className="text-sm text-gray-700 font-medium">{row.slideCount || row.items?.length || 0}</span>
      ),
    },
    {
      header: "SLIDE PRINCIPAL",
      accessor: (row: HeroCarouselRow) => {
        const primarySlide = row.primarySlide || row.items?.[0];
        return primarySlide?.title ? (
          <span className="text-sm text-gray-700">{primarySlide.title}</span>
        ) : (
          <span className="text-sm text-gray-400">Sin título</span>
        );
      },
    },
    {
      header: "IMAGEN",
      accessor: (row: HeroCarouselRow) => {
        const primarySlide = row.primarySlide || row.items?.[0];
        const imageUrl = primarySlide?.webImageUrl || primarySlide?.mobileImageUrl;
        return imageUrl ? (
          <img
            src={imageUrl}
            alt={primarySlide?.title || "Slide"}
            className="w-16 h-10 object-cover rounded border border-gray-200"
          />
        ) : (
          <span className="text-sm text-gray-400">Sin imagen</span>
        );
      },
    },
    {
      header: "ESTADO",
      accessor: (row: HeroCarouselRow) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.isActive
              ? "bg-gray-200 text-gray-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.isActive ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      header: "ACTUALIZADO",
      accessor: (row: HeroCarouselRow) =>
        new Date(row.updatedAt).toLocaleDateString("es-AR"),
    },
  ];

  const standardColumns = [
    {
      header: "NOMBRE",
      accessor: "name" as const,
    },
    {
      header: "DESCRIPCIÓN",
      accessor: (row: StandardCarouselRow) =>
        row.description ? (
          <span className="text-sm text-gray-600 line-clamp-2">
            {row.description}
          </span>
        ) : (
          <span className="text-sm text-gray-400">Sin descripción</span>
        ),
    },
    {
      header: "LAYOUT",
      accessor: (row: StandardCarouselRow) => (
        <span className="text-sm text-gray-700 capitalize">{row.layout}</span>
      ),
    },
    {
      header: "PRODUCTOS",
      accessor: (row: StandardCarouselRow) => (
        <span className="text-sm text-gray-700">{row.products.length}</span>
      ),
    },
    {
      header: "ORDEN",
      accessor: (row: StandardCarouselRow) => (
        <span className="text-sm text-gray-700">{row.order}</span>
      ),
    },
    {
      header: "ESTADO",
      accessor: (row: StandardCarouselRow) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.isActive
              ? "bg-gray-200 text-gray-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.isActive ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      header: "ACTUALIZADO",
      accessor: (row: StandardCarouselRow) =>
        new Date(row.updatedAt).toLocaleDateString("es-AR"),
    },
  ];

  // Acciones para cada fila
  const renderActions = (carousel: CarouselRow) => (
    <div className="flex space-x-2">
      <button
        onClick={() => handleEdit(carousel)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Editar carrusel"
      >
        <Icons.Edit size={16} />
      </button>
      <button
        onClick={() => handleToggleStatus(carousel)}
        className={`p-2 rounded-lg transition-colors ${
          carousel.isActive
            ? "text-red-600 hover:bg-red-50"
            : "text-[#282828] hover:bg-gray-100"
        }`}
        title={carousel.isActive ? "Desactivar carrusel" : "Activar carrusel"}
      >
        {carousel.isActive ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
      </button>
    </div>
  );

  const handleEdit = (carousel: CarouselRow) => {
    setEditingCarousel(carousel);
    setModalType(carousel.type);
    setIsModalOpen(true);
  };

  const handleToggleStatus = (carousel: CarouselRow) => {
    setCarouselToToggle(carousel);
    setIsStatusModalOpen(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!carouselToToggle) return;

    try {
      setIsSubmittingToggle(true);

      if (carouselToToggle.type === "hero") {
        await toggleHeroCarousel({
          id: carouselToToggle.id,
          isActive: carouselToToggle.isActive,
        });
      } else {
        await toggleStandardCarousel({
          id: carouselToToggle.id,
          isActive: carouselToToggle.isActive,
        });
      }

      const action = carouselToToggle.isActive ? "desactivado" : "activado";
      showSuccess(
        "Estado actualizado",
        `El carrusel "${carouselToToggle.type === "hero" ? carouselToToggle.name : carouselToToggle.name}" ha sido ${action} exitosamente.`,
        4000
      );
    } catch (error) {
      showError(
        "Error al actualizar",
        "No se pudo cambiar el estado del carrusel. Intenta nuevamente."
      );
    } finally {
      setIsSubmittingToggle(false);
      setIsStatusModalOpen(false);
      setCarouselToToggle(undefined);
    }
  };

  const handleCancelToggleStatus = () => {
    setIsStatusModalOpen(false);
    setCarouselToToggle(undefined);
  };

  const handleAddCarousel = () => {
    setModalType(activeTab);
    setEditingCarousel(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCarousel(undefined);
  };

  const handleInvalidateCache = () => {
    invalidateHomeCache(undefined, {
      onSuccess: (data) => {
        showSuccess(
          "Cache invalidado",
          data.message || "El cache de la home page ha sido invalidado exitosamente.",
          4000
        );
      },
      onError: (error) => {
        showError(
          "Error al invalidar cache",
          error instanceof Error ? error.message : "No se pudo invalidar el cache. Intenta nuevamente."
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Carruseles</h1>
          <p className="text-gray-600">
            Administra los carruseles hero y standard del sitio web.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === "hero" && (
            <button
              onClick={handleInvalidateCache}
              disabled={isInvalidatingCache}
              className="flex items-center px-4 py-2 bg-white text-[#282828] border border-[#282828] rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              title="Invalidar cache de home page"
            >
              <Icons.RefreshCw 
                size={20} 
                className={`mr-2 ${isInvalidatingCache ? "animate-spin" : ""}`} 
              />
              {isInvalidatingCache ? "Invalidando..." : "Invalidar Cache"}
            </button>
          )}
          <button
            onClick={handleAddCarousel}
            className="flex items-center px-4 py-2 bg-[#282828] text-white rounded-lg hover:bg-[#3a3a3a] transition-colors"
          >
            <Icons.Plus size={20} className="mr-2" />
            {activeTab === "hero" ? "Agregar Carrusel Hero" : "Agregar Carrusel Standard"}
          </button>
        </div>
      </div>

      <div className="flex space-x-2 border-b border-gray-200">
        {(["hero", "standard"] as TabKey[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? "border-[#282828] text-[#282828]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "hero" ? "Hero Carousels" : "Standard Carousels"}
          </button>
        ))}
      </div>

      {activeTab === "hero" ? (
        <AdminTable<HeroCarouselRow>
          searchable
          searchValue={searchQuery}
          onSearchChange={(value) => setSearchQuery(value)}
          searchPlaceholder="Buscar carruseles hero..."
          columns={heroColumns}
          data={heroCarousels}
          isLoading={isLoadingHero}
          isError={isErrorHero}
          error={heroError instanceof Error ? heroError.message : undefined}
          renderActions={renderActions}
          maxHeight="500px"
          pagination={
            heroPagination ? (
              <Pagination
                currentPage={heroPagination.page}
                totalPages={heroPagination.totalPages}
                totalItems={heroPagination.total}
                pageSize={heroPagination.limit}
                onPageChange={setHeroPage}
              />
            ) : undefined
          }
        />
      ) : (
        <AdminTable<StandardCarouselRow>
          searchable
          searchValue={searchQuery}
          onSearchChange={(value) => setSearchQuery(value)}
          searchPlaceholder="Buscar carruseles standard..."
          columns={standardColumns}
          data={standardCarousels}
          isLoading={isLoadingStandard}
          isError={isErrorStandard}
          error={standardError instanceof Error ? standardError.message : undefined}
          renderActions={renderActions}
          maxHeight="500px"
          pagination={
            standardPagination ? (
              <Pagination
                currentPage={standardPagination.page}
                totalPages={standardPagination.totalPages}
                totalItems={standardPagination.total}
                pageSize={standardPagination.limit}
                onPageChange={setStandardPage}
              />
            ) : undefined
          }
        />
      )}

      {isModalOpen && modalType === "hero" && (
        <HeroCarouselForm
          initialData={editingCarousel && editingCarousel.type === "hero" ? editingCarousel : undefined}
          existingCarousels={heroCarousels}
          onClose={handleCloseModal}
          onSuccess={({ title, description }) => {
            showSuccess(title, description, 4000);
          }}
          onError={({ title, description }) => {
            showError(title, description, 5000);
          }}
        />
      )}

      {isModalOpen && modalType === "standard" && (
        <StandardCarouselForm
          initialData={editingCarousel && editingCarousel.type === "standard" ? editingCarousel : undefined}
          existingCarousels={standardCarousels}
          onClose={handleCloseModal}
          onSuccess={({ title, description }) => {
            showSuccess(title, description, 4000);
          }}
          onError={({ title, description }) => {
            showError(title, description, 5000);
          }}
        />
      )}

      {/* Modal de confirmación de cambio de estado */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCancelToggleStatus}
        title={
          carouselToToggle?.isActive
            ? "¿Desactivar carrusel?"
            : "¿Activar carrusel?"
        }
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {carouselToToggle
              ? carouselToToggle.type === "hero"
                ? `${carouselToToggle.name} - Hero`
                : `${carouselToToggle.name} - Standard`
              : ""}{" "}
            - {carouselToToggle?.isActive ? "Activo" : "Inactivo"}
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelToggleStatus}
              className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmToggleStatus}
              disabled={isSubmittingToggle}
              className={`px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors ${
                isSubmittingToggle ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isSubmittingToggle ? "Actualizando..." : "Confirmar"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
